import CustomButton from 'Components/CustomButton'
import DeleteButton from 'Components/DeleteButton'
import DrawerRight from 'Components/DrawerRight'
import { WithLoader } from 'Components/WithLoader'
import {
  DOMICILE_CLASS_DRAWER_TAB_PARAM_KEY,
  PRODUCT_DETAILS_DRAWER_TAB_PARAM_KEY
} from 'Services/ProductManagement/constants'
import { useGetDomicileClassParameterGroupQuery } from 'Services/ProductManagement/Features/Parameter/parameterApi'
import ProductAvailability from 'Services/ProductManagement/Features/Product/Components/ProductAvailability'
import ProductCatalogs from 'Services/ProductManagement/Features/Product/Components/ProductCatalogs'
import { ProductDetails } from 'Services/ProductManagement/Features/Product/Components/ProductDetails'
import ProductPricing from 'Services/ProductManagement/Features/Product/Components/ProductPricing'
import { SELECTED_PRODUCT_PARAMETER_KEY } from 'Services/ProductManagement/Features/Product/Hooks/useProductsWithParams'
import { PricingTabType } from 'Services/ProductManagement/Features/Product/productSlice'
import { useSelectedTariff } from 'Services/ProductManagement/Features/Tariff/Hooks'
import { TariffProductTabType } from 'Services/ProductManagement/Features/Tariff/types'
import { useGetSingleVatDefinitionQuery } from 'Services/ProductManagement/Features/VATDefinition/vatDefinitionApi'
import useTypedSelector from 'Store'
import { GridHighlightProvider } from 'Util/Context/gridHighlightContext'
import { usePreviousLocation } from 'Util/Context/previousLocationContext'
import { useUnsavedChanges } from 'Util/Context/unsavedChangesContext'
import {
  IS_HSL_SERVICE_ENVIRONMENT,
  IS_TARIFF_EDIT_ALLOWED,
  IS_WALTTI_SERVICE_ENVIRONMENT,
  PRODUCT_MANAGEMENT_ROOT_URL
} from 'Util/constants'
import { useConfirm } from 'Util/hooks/useConfirm'
import EditIcon from '@mui/icons-material/Edit'
import { TabContext, TabList, TabPanel } from '@mui/lab'
import { Box, Divider, Tab } from '@mui/material'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { t } from 'ttag'

interface TariffProductDrawerProps {
  open: boolean
  handleClose: () => void
  handleProductDelete: (productId: string) => void
}

export function TariffProductDrawer({
  open,
  handleClose,
  handleProductDelete
}: Readonly<TariffProductDrawerProps>) {
  const baseUrl = `${PRODUCT_MANAGEMENT_ROOT_URL}/tariffs`
  const navigate = useNavigate()
  const { id: tariffId } = useParams()
  const { confirm } = useConfirm()
  const [searchParams, setSearchParams] = useSearchParams()
  const { singleTariff } = useSelectedTariff(tariffId)
  const { confirmForUnsavedChanges } = useUnsavedChanges()

  const { data: vatDefinition = null, isLoading: isVatDefinitionsLoading } =
    useGetSingleVatDefinitionQuery(singleTariff?.vat_definition_id ?? '', {
      skip: !open || !singleTariff?.vat_definition_id
    })

  const {
    data: domicileClassParameterGroup = null,
    isLoading: isDomicileClassParameterGroupLoading
  } = useGetDomicileClassParameterGroupQuery(undefined, {
    skip: !open || IS_WALTTI_SERVICE_ENVIRONMENT // Waltti does not use domicile class based pricing
  })

  const productDetailsDrawerTabFromParams =
    searchParams.get(PRODUCT_DETAILS_DRAWER_TAB_PARAM_KEY) ?? TariffProductTabType.DETAILS
  const { singleProduct, singleLoading: singleProductLoading } = useTypedSelector(
    state => state.product
  )

  const previousLocation = usePreviousLocation(
    `${baseUrl}?${SELECTED_PRODUCT_PARAMETER_KEY}=${singleProduct?.id}`
  )

  const productDeleteModalMessage = () => {
    if (singleProduct && singleProduct.catalog_ids.length > 0) {
      return t`This product has not been removed from all catalogs, are you sure you want to delete the product?`
    }
    return t`Are you sure you want to delete the product?`
  }

  const handleProductDetailsDrawerTabChange = (
    _event: React.SyntheticEvent,
    newValue: TariffProductTabType
  ) => {
    confirmForUnsavedChanges(() => {
      searchParams.set(PRODUCT_DETAILS_DRAWER_TAB_PARAM_KEY, newValue)
      if (newValue !== TariffProductTabType.PRICING) {
        searchParams.delete(DOMICILE_CLASS_DRAWER_TAB_PARAM_KEY)
      }
      setSearchParams(searchParams)
    })
  }

  const handleDrawerClose = () => {
    confirmForUnsavedChanges(() => {
      handleClose()
    })
  }

  /**
   * In Waltti environment only Exchange period is shown on tariff product level.
   * HSL environment has only Pricing enabled.
   */
  const allowedPricingTabs = IS_WALTTI_SERVICE_ENVIRONMENT
    ? [PricingTabType.EXCHANGE_PERIOD]
    : [PricingTabType.PRICINGS]

  const isSingleProductLoading = singleProductLoading === 'pending'

  const showProductDeleteButton = singleProduct && IS_TARIFF_EDIT_ALLOWED
  const showProductEditButton = singleProduct && IS_TARIFF_EDIT_ALLOWED

  return (
    <DrawerRight
      open={open}
      onClose={handleDrawerClose}
      topBar={
        <Box>
          {showProductDeleteButton && (
            <DeleteButton
              sx={{ mr: 1 }}
              size="small"
              onClick={() =>
                confirm({
                  title: t`Are you sure?`,
                  message: productDeleteModalMessage(),
                  handleConfirm: () => {
                    handleProductDelete(singleProduct.id)
                  }
                })
              }
            >{t`Delete`}</DeleteButton>
          )}
          {showProductEditButton && (
            <CustomButton
              onClick={() => navigate(`${baseUrl}/${tariffId}/products/${singleProduct.id}/edit`)}
              size="small"
              startIcon={<EditIcon />}
            >{t`Edit`}</CustomButton>
          )}
        </Box>
      }
    >
      <TabContext value={productDetailsDrawerTabFromParams}>
        <TabList
          onChange={handleProductDetailsDrawerTabChange}
          aria-label={t`Tariff product tab list`}
        >
          <Tab label={t`Product details`} value={TariffProductTabType.DETAILS} />
          <Tab
            label={IS_HSL_SERVICE_ENVIRONMENT ? t`Pricing` : t`Exchange period`}
            value={TariffProductTabType.PRICING}
          />
          <Tab
            label={t`Product's sales channels`}
            value={TariffProductTabType.SALES_CHANNELS}
            sx={{ display: IS_HSL_SERVICE_ENVIRONMENT ? 'flex' : 'none' }}
          />
          <Tab label={t`Availability`} value={TariffProductTabType.AVAILABILITY} />
        </TabList>
        <Divider />
        <TabPanel sx={{ p: 0 }} value={TariffProductTabType.DETAILS}>
          <WithLoader loading={isSingleProductLoading}>
            {singleProduct && (
              <ProductDetails
                product={singleProduct}
                vatDefinition={vatDefinition}
                isVatDefinitionsLoading={isVatDefinitionsLoading}
              />
            )}
          </WithLoader>
        </TabPanel>
        <TabPanel sx={{ p: 0 }} value={TariffProductTabType.PRICING}>
          <WithLoader loading={isSingleProductLoading || isDomicileClassParameterGroupLoading}>
            {singleProduct && (
              <GridHighlightProvider>
                <ProductPricing
                  product={singleProduct}
                  domicileClassParameterGroup={domicileClassParameterGroup}
                  allowedTabs={allowedPricingTabs}
                />
              </GridHighlightProvider>
            )}
          </WithLoader>
        </TabPanel>
        {IS_HSL_SERVICE_ENVIRONMENT && (
          <TabPanel sx={{ p: 0 }} value={TariffProductTabType.SALES_CHANNELS}>
            <WithLoader loading={isSingleProductLoading}>
              {singleProduct && (
                <ProductCatalogs product={singleProduct} handleClose={handleClose} />
              )}
            </WithLoader>
          </TabPanel>
        )}
        <TabPanel sx={{ p: 0 }} value={TariffProductTabType.AVAILABILITY}>
          <WithLoader loading={isSingleProductLoading}>
            {singleProduct && (
              <ProductAvailability
                product={singleProduct}
                successBaseUrl={previousLocation}
                handleClose={handleDrawerClose}
              />
            )}
          </WithLoader>
        </TabPanel>
      </TabContext>
    </DrawerRight>
  )
}
